import { Cloneable } from "./cloneable";
import { Serializable } from "./serializable";
import { Stylist } from "./stylist/stylist.model";

/**
 * The three things one stylist can let another do with their calendar.
 * Ordered: editCalendar implies viewBookingDetail, which implies viewCalendar.
 */
export interface ConnectionPermissions {
  viewCalendar: boolean;
  editCalendar: boolean;
  viewBookingDetail: boolean;
}

export type ConnectionPermissionKey = keyof ConnectionPermissions;

/**
 * Applies the permission hierarchy after one toggle changes and returns a
 * new, coherent triple. Clearing a permission clears everything above it;
 * raising one raises everything below it. Never mutates its input.
 *
 * Takes a bare triple rather than a `Connection` because the invite modal
 * and the accept step both run before any connection exists. The clearing
 * half mirrors the toggle handlers in `ConnectionDetailComponent`; the
 * raising half is the API's `normalisePermissions` line for line, so every
 * triple this returns is one the server stores unchanged and what the sender
 * sees on screen is what gets saved.
 */
export function cascadePermissions(
  permissions: ConnectionPermissions,
  changed: ConnectionPermissionKey
): ConnectionPermissions {
  const next: ConnectionPermissions = {
    viewCalendar: permissions.viewCalendar,
    viewBookingDetail: permissions.viewBookingDetail,
    editCalendar: permissions.editCalendar,
  };

  // Clearing: everything above the cleared permission goes with it.
  if (!next[changed]) {
    if (changed === 'viewCalendar') {
      next.viewBookingDetail = false;
      next.editCalendar = false;
    } else if (changed === 'viewBookingDetail') {
      next.editCalendar = false;
    }
  }

  // Raising: the server's rule, in the server's order.
  if (next.editCalendar) {
    next.viewBookingDetail = true;
  }
  if (next.viewBookingDetail) {
    next.viewCalendar = true;
  }

  return next;
}

export interface ConnectionJSON {
  id: number;
  stylistID: number;
  connectedStylistID: number; // the stylist which is connected to
  connectedStylist: Stylist;
  createdAt: Date;
  grantPermissions: {
    viewCalendar: boolean;
    editCalendar: boolean;
    viewBookingDetail: boolean;
  };
  grantedPermissions: {
    viewCalendar: boolean;
    editCalendar: boolean;
    viewBookingDetail: boolean;
  };
  showOnCalendar: boolean; // true if the connection should be displayed on the calendar, false otherwise
  connectedSalonName: string;
  /**
   * Whether the colleague on the other end still passes the server's
   * connect-accounts gate. False means their administrator revoked the
   * permission, or they were deactivated or deleted — the row and its grant
   * bits are untouched, they are simply unreachable until that is undone.
   *
   * Optional because an API that predates the field sends nothing; see
   * `Connection.parse`, which reads an absent value as connectable.
   */
  counterpartyCanConnect?: boolean;
}

export interface ConnectionSerialized {
  id: number;
  stylistID: number;
  connectedStylistID: number; // the stylist which is connected to
  connectedStylist: Stylist;
  createdAt: Date;
  grantPermissions: {
    viewCalendar: boolean;
    editCalendar: boolean;
    viewBookingDetail: boolean;
  };
  grantedPermissions: {
    viewCalendar: boolean;
    editCalendar: boolean;  
    viewBookingDetail: boolean;
  };
  showOnCalendar: boolean; // true if the connection should be displayed on the calendar, false otherwise
  connectedSalonName: string;
  counterpartyCanConnect: boolean;
}

export class Connection
  implements Cloneable<Connection, ConnectionJSON>, Serializable<ConnectionSerialized> {

  public id: number;
  public stylistID: number;
  public connectedStylistID: number; // the stylist which is connected to
  public connectedStylist: Stylist;
  public createdAt: Date;
  public grantPermissions: {
    viewCalendar: boolean;
    editCalendar: boolean;
    viewBookingDetail: boolean;
  };
  readonly grantedPermissions: {
    viewCalendar: boolean;
    editCalendar: boolean;
    viewBookingDetail: boolean;
  };
  public showOnCalendar: boolean; // true if the connection should be displayed on the calendar, false otherwise
  public connectedSalonName: string;

  /**
   * False when the colleague on the other end currently fails the server's
   * connect-accounts gate — revoked, deactivated or deleted. The connection
   * and its grants survive; they are unreachable until it is restored, so
   * every surface labels them rather than dropping them.
   */
  public counterpartyCanConnect: boolean;

  constructor(options: ConnectionJSON) {
    this.id = options.id;
    this.stylistID = options.stylistID;
    this.connectedStylistID = options.connectedStylistID;
    this.connectedStylist = options.connectedStylist;
    this.createdAt = options.createdAt;
    this.grantPermissions = options.grantPermissions;
    this.grantedPermissions = options.grantedPermissions;
    this.showOnCalendar = options.showOnCalendar;
    this.connectedSalonName = options.connectedSalonName;
    // Same fail-open default as parse(), for the same reason: a Connection
    // built from an object that never carried the field — clone(), a test
    // fixture, an older cached payload — is connectable, not hidden.
    this.counterpartyCanConnect = options.counterpartyCanConnect ?? true;
  }

  public static parse(params: Record<string, any>): Connection {
    return new Connection({
      id: params['id'],
      stylistID: params['stylistID'],
      connectedStylistID: params['connectedStylistID'],
      connectedStylist: Stylist.parseStylistData(params['connectedStylist']),
      createdAt: params['createdAt'],
      grantPermissions: params['grantPermissions'],
      grantedPermissions: params['grantedPermissions'],
      showOnCalendar: params['showOnCalendar'],
      connectedSalonName: params['connectedSalonName'],
      // `counterpartyCanConnect` is the API's spelling, read verbatim — the
      // two repos ship separately and nothing checks this name for us.
      //
      // The default is deliberately the opposite of the fail-closed default
      // used elsewhere on this feature. `Boolean(undefined)` is false, and an
      // app talking to an API that predates the field would then label every
      // colleague unavailable — worse than the status quo, because the app
      // cannot tell "not connectable" from "old server". MySQL hands the
      // present value back as 0/1, so it still needs coercing.
      counterpartyCanConnect: Boolean(params['counterpartyCanConnect'] ?? true)
    });
  }

  public toJSON(): ConnectionJSON {
    return {
      id: this.id,
      stylistID: this.stylistID,
      connectedStylistID: this.connectedStylistID,
      connectedStylist: this.connectedStylist,
      createdAt: this.createdAt,
      grantPermissions: this.grantPermissions,
      grantedPermissions: this.grantedPermissions,
      showOnCalendar: this.showOnCalendar,
      connectedSalonName: this.connectedSalonName,
      counterpartyCanConnect: this.counterpartyCanConnect
    };
  }

  public clone(): Connection {
    return new Connection(this.toJSON());
  }

  public serialize(): ConnectionSerialized {
    return {
      id: this.id,
      stylistID: this.stylistID,
      connectedStylistID: this.connectedStylistID,
      connectedStylist: this.connectedStylist,
      createdAt: this.createdAt,
      grantPermissions: this.grantPermissions,
      grantedPermissions: this.grantedPermissions,
      showOnCalendar: this.showOnCalendar,
      connectedSalonName: this.connectedSalonName,
      counterpartyCanConnect: this.counterpartyCanConnect
    };
  }
}


export interface ConnectionRequestJSON {
  id: number;
  fromStylistID: number; // the stylist which sent the request
  fromStylist: Stylist | null; // the stylist which sent the request
  toStylistID: number; // the stylist which received the request
  toStylist: Stylist | null; // the stylist which received the request
  toEmail: string; // the email of the stylist which received the request
  createdAt: Date;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  // What the sender chose to share when composing the request. Read by the
  // accept step so the recipient can see the grant before choosing their own.
  viewCalendar: boolean;
  editCalendar: boolean;
  viewBookingDetail: boolean;
}

export interface ConnectionRequestSerialized {
  id: number;
  fromStylistID: number; // the stylist which sent the request
  fromStylist: Stylist | null; // the stylist which sent the request
  toStylistID: number; // the stylist which received the request
  toStylist: Stylist | null; // the stylist which received the request
  toEmail: string; // the email of the stylist which received the request
  createdAt: Date;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  // What the sender chose to share when composing the request. Read by the
  // accept step so the recipient can see the grant before choosing their own.
  viewCalendar: boolean;
  editCalendar: boolean;
  viewBookingDetail: boolean;
}

export class ConnectionRequest
  implements Cloneable<ConnectionRequest, ConnectionRequestJSON>, Serializable<ConnectionRequestSerialized> {

  public id: number;
  public fromStylistID: number; // the stylist which sent the request
  public fromStylist: Stylist | null; // the stylist which sent the request
  public toStylistID: number; // the stylist which received the request
  public toStylist: Stylist | null; // the stylist which received the request
  public toEmail: string; // the email of the stylist which received the request
  public createdAt: Date;
  public status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  // What the sender chose to share when composing the request.
  public viewCalendar: boolean;
  public editCalendar: boolean;
  public viewBookingDetail: boolean;


  constructor(options: ConnectionRequestJSON) {
    this.id = options.id;
    this.fromStylistID = options.fromStylistID;
    this.fromStylist = options.fromStylist;
    this.toStylistID = options.toStylistID;
    this.toStylist = options.toStylist;
    this.toEmail = options.toEmail;
    this.createdAt = options.createdAt;
    this.status = options.status;
    this.viewCalendar = options.viewCalendar;
    this.editCalendar = options.editCalendar;
    this.viewBookingDetail = options.viewBookingDetail;
  }


  public static parse(params: Record<string, any>): ConnectionRequest {
    return new ConnectionRequest({
      id: params['id'],
      fromStylistID: params['fromStylistID'],
      fromStylist: params['fromStylist'] ? Stylist.parseStylistData(params['fromStylist']) : null,
      toStylistID: params['toStylistID'],
      toStylist: params['toStylist'] ? Stylist.parseStylistData(params['toStylist']) : null,
      toEmail: params['toEmail'],
      createdAt: params['createdAt'],
      status: params['status'],
      // The API nests the sender's choice under `grantPermissions` on every
      // response that carries a request — the list, the create and the
      // accept. Reading the three names flat off the payload silently gave
      // `undefined` for all of them, so the accept step told every recipient
      // the sender was sharing nothing, whatever they had actually chosen.
      // The flat fallback stays for a payload that predates the nesting;
      // MySQL hands these back as 0/1, so both shapes need coercing.
      viewCalendar: Boolean(params['grantPermissions']?.['viewCalendar'] ?? params['viewCalendar']),
      editCalendar: Boolean(params['grantPermissions']?.['editCalendar'] ?? params['editCalendar']),
      viewBookingDetail: Boolean(
        params['grantPermissions']?.['viewBookingDetail'] ?? params['viewBookingDetail']
      ),
    });
  }

  public toJSON(): ConnectionRequestJSON {
    return {
      id: this.id,
      fromStylistID: this.fromStylistID,
      fromStylist: this.fromStylist,
      toStylistID: this.toStylistID,
      toStylist: this.toStylist,
      toEmail: this.toEmail,
      createdAt: this.createdAt,
      status: this.status,
      viewCalendar: this.viewCalendar,
      editCalendar: this.editCalendar,
      viewBookingDetail: this.viewBookingDetail
    };
  }

  /**
   * True when this request is waiting on `stylistID` to answer it.
   *
   * Scoped to the recipient deliberately. A request you sent is pending on
   * somebody else, and an email-only invitation carries a null `toStylistID`
   * until the invitee registers an account, so neither counts as an action
   * waiting on you. Shared by the side-menu badge and the connections page's
   * Requests badge so the two can never drift apart.
   */
  public isPendingFor(stylistID: number): boolean {
    return this.status === 'pending' && this.toStylistID === stylistID;
  }

  public clone(): ConnectionRequest {
    return new ConnectionRequest(this.toJSON());
  }

  public serialize(): ConnectionRequestSerialized {
    return {
      id: this.id,
      fromStylistID: this.fromStylistID,
      fromStylist: this.fromStylist,
      toStylistID: this.toStylistID,
      toStylist: this.toStylist,
      toEmail: this.toEmail,
      createdAt: this.createdAt,
      status: this.status,
      viewCalendar: this.viewCalendar,
      editCalendar: this.editCalendar,
      viewBookingDetail: this.viewBookingDetail
    };
  }
}